// src/components/ScheduledChecksView.jsx
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { collection, query, getDocs, where } from "firebase/firestore";
import { db, auth } from "../firebase";
import { getScheduledChecksForUser, formatDueDate } from "../utils/scheduleUtils";

export default function ScheduledChecksView({ companyId, onStartCheck }) {
  const { t } = useTranslation();
  const [scheduledChecks, setScheduledChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userGroups, setUserGroups] = useState([]);

  useEffect(() => {
    if (!companyId || !auth.currentUser) return;

    async function loadScheduledChecks() {
      try {
        setLoading(true);

        // Get user's groups
        const userDoc = await getDocs(
          query(collection(db, "users"), where("uid", "==", auth.currentUser.uid))
        );
        const userData = userDoc.docs[0]?.data();
        const groups = userData?.groups || [];
        setUserGroups(groups);

        // Get all forms with their schedules
        const formsRef = collection(db, `companies/${companyId}/forms`);
        const formsSnap = await getDocs(formsRef);

        const formsWithSchedules = [];
        for (const formDoc of formsSnap.docs) {
          const formData = formDoc.data();
          
          // Get schedules for this form
          const schedulesRef = collection(db, `companies/${companyId}/forms/${formDoc.id}/schedules`);
          const schedulesSnap = await getDocs(schedulesRef);
          
          const schedules = schedulesSnap.docs.map(scheduleDoc => ({
            id: scheduleDoc.id,
            ...scheduleDoc.data()
          }));

          if (schedules.length > 0) {
            formsWithSchedules.push({
              id: formDoc.id,
              title: formData.title,
              schedules
            });
          }
        }

        // Filter and sort scheduled checks for this user
        const checks = getScheduledChecksForUser(
          formsWithSchedules,
          auth.currentUser.uid,
          groups
        );

        setScheduledChecks(checks);
      } catch (err) {
        console.error("Error loading scheduled checks:", err);
      } finally {
        setLoading(false);
      }
    }

    loadScheduledChecks();

    // Refresh every minute to update status
    const interval = setInterval(loadScheduledChecks, 60000);
    return () => clearInterval(interval);
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600 mb-2"></div>
          <p className="text-sm text-gray-500">{t('checks.loadingScheduled')}</p>
        </div>
      </div>
    );
  }

  if (scheduledChecks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-100">
        <div className="text-6xl mb-4">📅</div>
        <h3 className="text-xl font-semibold text-gray-100 mb-2">
          {t('checks.noScheduledChecks')}
        </h3>
        <p className="text-sm text-gray-200 mb-2">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
        <p className="text-sm text-gray-300 text-center max-w-md">
          {t('checks.noScheduledChecksMessage')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
          {t('checks.scheduledChecks')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('checks.checksAvailable', { count: scheduledChecks.length })}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scheduledChecks.map((check) => {
          const statusColors = {
            overdue: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",
            dueToday: "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800",
            dueTomorrow: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
            availableEarly: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
            upcoming: "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700",
          };

          const badgeColors = {
            overdue: "bg-red-500",
            dueToday: "bg-orange-500",
            dueTomorrow: "bg-blue-500",
            availableEarly: "bg-green-500",
            upcoming: "bg-gray-500",
          };

          return (
            <div
              key={`${check.formId}-${check.scheduleId}`}
              className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                statusColors[check.status.type]
              }`}
            >
              {/* Status Badge */}
              <div className="flex items-center justify-between mb-3">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold text-white ${
                    badgeColors[check.status.type]
                  }`}
                >
                  {check.status.label}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {check.frequency}
                </span>
              </div>

              {/* Form Title */}
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
                {check.formTitle}
              </h3>

              {/* Schedule Description */}
              {check.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  {check.description}
                </p>
              )}

              {/* Due Date */}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mb-4">
                <span>📅</span>
                <span>{formatDueDate(check.dueDate)}</span>
              </div>

              {/* Window Info */}
              {(check.windowBefore > 0 || check.windowAfter > 0) && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  {t('checks.acceptanceWindow', { before: check.windowBefore, after: check.windowAfter })}
                </p>
              )}

              {/* Conduct Button */}
              <button
                onClick={() => onStartCheck(check.formId, check.scheduleId)}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                {t('checks.conductCheckNow')}
              </button>

              {/* Last Completed */}
              {check.lastCompleted && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  {t('checks.lastCompleted')}{': '}
                  {new Date(check.lastCompleted.toDate()).toLocaleDateString()}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
